import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { api } from 'services'

/**
 * The twelve setup predicates, derived SERVER-side (VRG-338).
 *
 * Mirrors `SetupChecklistStatus` in
 * `apps/api/src/application/use-cases/company/setup-completion-reward/setup-completion-eligibility.ts`
 * key-for-key. The two MUST change together — the server owns the block→predicate mapping
 * because it decides which ledger row a reward writes, and a free activation is money.
 */
export interface SetupChecklistStatus {
  // Block 1 — `setup`
  product: boolean
  branding: boolean
  installation: boolean
  activationRules: boolean
  // Block 2 — `first_certificate`
  payment: boolean
  tags: boolean
  certificate: boolean
  certificateActive: boolean
  // Block 3 — `working`
  firstScan: boolean
  firstLead: boolean
  firstTransfer: boolean
  askVera: boolean
}

export type SetupBlockId = 'setup' | 'first_certificate' | 'working'

export interface SetupRewardBlock {
  id: SetupBlockId
  keys: (keyof SetupChecklistStatus)[]
  completed: number
  total: number
  /** All four true — the grant would succeed. */
  eligible: boolean
  /** This block's activation has already been credited. */
  earned: boolean
}

export interface SetupCompletionRewardStatus {
  /** Legacy single-grant flag, kept for back-compat. Prefer `blocks[].earned`. */
  earned: boolean
  eligible: boolean
  status: SetupChecklistStatus
  blocks: SetupRewardBlock[]
  /** Blocks that are eligible but not yet credited — exactly what a POST would grant. */
  claimableBlocks: SetupBlockId[]
}

export interface SetupCompletionRewardGrant {
  /** This call inserted at least one ledger row. */
  granted: boolean
  /** Which blocks THIS call credited — empty when there was nothing to give. */
  grantedBlocks: SetupBlockId[]
  status: SetupChecklistStatus
  /** Post-write state; each block's `earned` already reflects the insert. */
  blocks: SetupRewardBlock[]
  newBalance: number
}

export const SETUP_REWARD_QUERY_KEY = ['company', 'setup-completion-reward'] as const

const ENDPOINT = '/companies/admin/me/setup-completion-reward'

/**
 * Whether the company has earned its free activation for finishing setup (VRG-338).
 *
 * READ-ONLY — never writes, so it is safe to mount anywhere, including on surfaces that only
 * want to render the earned state.
 *
 * The predicates come back from the SERVER, re-derived there rather than trusted from the
 * client, because a free activation is money. The client's own `useHomeChecklist` is for
 * rendering; this is for deciding.
 */
export const useSetupCompletionRewardStatus = (options?: { enabled?: boolean }) =>
  useQuery<SetupCompletionRewardStatus>({
    queryKey: SETUP_REWARD_QUERY_KEY,
    queryFn: async () => (await api.get(ENDPOINT)).data,
    enabled: options?.enabled !== false,
  })

/**
 * Claim the reward.
 *
 * Idempotent server-side — a partial unique index on (company_id, earned_source) is the real
 * guarantee, so a double-fire returns `granted: false` rather than crediting twice.
 *
 * TAKES THE BLOCK IT MEANS. The endpoint has always accepted an optional `block` (see
 * `GrantSetupCompletionRewardRequestBodyDto`); this hook never passed one, so every claim was
 * a BLANKET claim of every eligible-and-unearned block. That was harmless while the caller
 * was a fire-and-forget effect, and is wrong now that the caller is a BUTTON sitting inside
 * one specific block: a blanket claim from block 1's button would also silently credit block
 * 3, whose own button then vanishes having never been pressed. A control should do the thing
 * it is labelled with and nothing else.
 *
 * Omitting the variable keeps the old blanket behaviour, so this is additive.
 *
 * 403 when the named block's predicates do not all hold. That is expected, not exceptional:
 * the client derives its own view of completeness a beat before the server agrees, so a
 * rejection means "not yet", not "something is wrong".
 */
export const useGrantSetupCompletionReward = () => {
  const queryClient = useQueryClient()

  return useMutation<SetupCompletionRewardGrant, unknown, SetupBlockId | undefined>({
    mutationFn: async (block) => (await api.post(ENDPOINT, block ? { block } : {})).data,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: SETUP_REWARD_QUERY_KEY })
    },
  })
}
